import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface QueueStatus {
  queue: {
    inQueue: number;
    inProgress: number;
    failed: number;
    total: number;
  };
  processing: {
    activeConversions: number;
    maxConcurrent: number;
    availableSlots: number;
    queueCheckIntervalHours: number;
  };
  stats: {
    completedToday: number;
    failedToday: number;
  };
}

export interface QueueMetrics {
  activeMessageCount: number;
  inProgressCount: number;
  failedCount: number;
  totalMessageCount: number;
}

export interface ActiveConversion {
  id: string;
  name: string;
  tenant: string;
  source?: string;
  /** Display name of the tenant matched on `tenant` + `source`, or `tenant` when it has none. */
  tenantDisplayName?: string;
  containerForConversion: string;
  statusOFProcessedFiles: number;
  fileCount: number;
  startedAt: Date;
  duration: string;
  files: ConversionFile[];
}

export interface ConversionFile {
  id: string;
  name: string;
  extension: string;
  sizeOfFile: number;
  statusOFProcessedFile: number;
  conversionStartedAt: Date;
  conversionRetryCount: number;
}

export interface FailedUpload {
  id: string;
  name: string;
  tenant: string;
  containerForConversion: string;
  uploadDate: Date;
  fileCount: number;
}

export interface ActiveConversionsResponse {
  totalActive: number;
  conversions: ActiveConversion[];
}

export interface PaginationQueryActiveConversions {
  pageNumber: number;
  pageSize: number;
  search?: string;
  orderBy?: string;
  filterByStatus?: number[];
  filterByTenant?: string[];
  dateFrom?: string;
  dateTo?: string;
  filterByContainer?: string;
}

export interface ActiveConversionsPaged {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  conversions: ActiveConversion[];
}

@Injectable({
  providedIn: 'root'
})
export class VideoQueueService {
  private apiUrl = `${environment.apiUrl}admin/video-queue`;

  constructor(private http: HttpClient) {}

  getQueueStatus(): Observable<QueueStatus> {
    return this.http.get<QueueStatus>(`${this.apiUrl}/status`);
  }

  getQueueMetrics(): Observable<QueueMetrics> {
    return this.http.get<QueueMetrics>(`${this.apiUrl}/metrics`);
  }

  getActiveConversions(query: PaginationQueryActiveConversions): Observable<ActiveConversionsPaged> {
    return this.http.post<ActiveConversionsPaged>(`${this.apiUrl}/active-conversions`, query);
  }

  getCompletedToday(): Observable<{ completedToday: number }> {
    return this.http.get<{ completedToday: number }>(`${this.apiUrl}/completed-today`);
  }

  /**
   * Get all failed uploads
   */
  getFailedUploads(): Observable<FailedUpload[]> {
    return this.http.get<FailedUpload[]>(`${this.apiUrl}/failed`);
  }

  /**
   * Retry a failed upload by moving it back to the queue
   */
  retryFailedUpload(uploadId: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.apiUrl}/failed/${uploadId}/retry`, {});
  }
}
